import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase/config';
import '../styles/AllOrdersManagement.css';

const AllOrdersManagement = () => {
  const [regularOrders, setRegularOrders] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('regular');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchAllOrders();
  }, []);

  const fetchAllOrders = async () => {
    setLoading(true);
    try {
      // Fetch regular orders
      const ordersQuery = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        type: 'regular'
      }));

      // Fetch custom orders
      const customOrdersQuery = query(collection(db, 'customOrders'), orderBy('createdAt', 'desc'));
      const customOrdersSnapshot = await getDocs(customOrdersQuery);
      const customOrdersData = customOrdersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        type: 'custom'
      }));

      setRegularOrders(ordersData);
      setCustomOrders(customOrdersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert('Error loading orders: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus, orderType) => {
    try {
      const collectionName = orderType === 'regular' ? 'orders' : 'customOrders';
      const orderRef = doc(db, collectionName, orderId);
      await updateDoc(orderRef, { status: newStatus });
      
      // Update local state
      if (orderType === 'regular') {
        setRegularOrders(prev => 
          prev.map(order => 
            order.id === orderId ? { ...order, status: newStatus } : order
          )
        );
      } else {
        setCustomOrders(prev => 
          prev.map(order => 
            order.id === orderId ? { ...order, status: newStatus } : order
          )
        );
      }
      
      alert('Order status updated successfully!');
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Error updating status: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'confirmed': return '#27ae60';
      case 'in-progress': return '#3498db';
      case 'completed': return '#2ecc71';
      case 'cancelled': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const filterOrders = (orders) => {
    return orders.filter(order => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        order.userEmail?.toLowerCase().includes(searchLower) ||
        order.email?.toLowerCase().includes(searchLower) ||
        order.phone?.includes(searchQuery) ||
        order.shippingInfo?.phone?.includes(searchQuery) ||
        order.shippingInfo?.fullName?.toLowerCase().includes(searchLower) ||
        order.userName?.toLowerCase().includes(searchLower) ||
        order.id.toLowerCase().includes(searchLower);
      
      const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  };

  const filteredRegularOrders = filterOrders(regularOrders);
  const filteredCustomOrders = filterOrders(customOrders);

  if (loading) {
    return (
      <div className="all-orders-management">
        <div className="loading">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="all-orders-management">
      <div className="orders-header">
        <h2>All Orders Management</h2>
        <button onClick={fetchAllOrders} className="btn-refresh">
          🔄 Refresh
        </button>
      </div>

      <div className="orders-tabs">
        <button 
          className={`tab-btn ${activeTab === 'regular' ? 'active' : ''}`}
          onClick={() => setActiveTab('regular')}
        >
          Regular Orders ({regularOrders.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'custom' ? 'active' : ''}`}
          onClick={() => setActiveTab('custom')}
        >
          Custom Orders ({customOrders.length})
        </button>
      </div>

      <div className="orders-filters">
        <input
          type="text"
          placeholder="🔍 Search by name, email, phone, or order ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="in-progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {activeTab === 'regular' && (
        <div className="orders-section">
          <h3>Regular Orders ({filteredRegularOrders.length})</h3>
          {filteredRegularOrders.length === 0 ? (
            <p className="no-orders">No regular orders found.</p>
          ) : (
            <div className="orders-grid">
              {filteredRegularOrders.map(order => (
                <div key={order.id} className="order-card">
                  <div className="order-card-header">
                    <span className="order-id">#{order.id.slice(-8)}</span>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(order.status) }}
                    >
                      {order.status}
                    </span>
                  </div>

                  <div className="order-info">
                    <p><strong>Customer Name:</strong> {order.shippingInfo?.fullName || order.userName || 'N/A'}</p>
                    <p><strong>Email:</strong> {order.shippingInfo?.email || order.userEmail}</p>
                    <p><strong>Phone:</strong> {order.shippingInfo?.phone || 'N/A'}</p>
                    <p><strong>Address:</strong> {order.shippingInfo?.address || 'N/A'}</p>
                    <p><strong>City:</strong> {order.shippingInfo?.city || 'N/A'}, <strong>State:</strong> {order.shippingInfo?.state || 'N/A'}</p>
                    <p><strong>Pincode:</strong> {order.shippingInfo?.pincode || 'N/A'}</p>
                    {order.shippingInfo?.landmark && <p><strong>Landmark:</strong> {order.shippingInfo.landmark}</p>}
                    <p><strong>Order Date:</strong> {order.createdAt?.toLocaleString()}</p>
                    <p><strong>Total Amount:</strong> ₹{order.total?.toFixed(2)}</p>
                    <p><strong>Payment Method:</strong> {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Payment'}</p>
                    <p><strong>Payment Status:</strong> <span className={`payment-status ${order.paymentStatus}`}>{order.paymentStatus || 'pending'}</span></p>
                  </div>

                  <div className="order-items">
                    <strong>Items:</strong>
                    <ul>
                      {order.items?.map((item, idx) => (
                        <li key={idx}>
                          {item.displayName || item.name}
                          {item.variant && ` (${item.variant.name})`}
                          {' - '}Qty: {item.quantity}
                          {' - '}₹{(item.price * item.quantity).toFixed(2)}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="order-actions">
                    <select
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value, 'regular')}
                      className="status-select"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'custom' && (
        <div className="orders-section">
          <h3>Custom Orders ({filteredCustomOrders.length})</h3>
          {filteredCustomOrders.length === 0 ? (
            <p className="no-orders">No custom orders found.</p>
          ) : (
            <div className="orders-grid">
              {filteredCustomOrders.map(order => (
                <div key={order.id} className="order-card custom-order-card">
                  <div className="order-card-header">
                    <span className="order-id">#{order.id.slice(-8)}</span>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(order.status) }}
                    >
                      {order.status}
                    </span>
                  </div>

                  <div className="order-info">
                    <p><strong>Customer:</strong> {order.email}</p>
                    <p><strong>Phone:</strong> {order.phone}</p>
                    <p><strong>Event Type:</strong> {order.eventType}</p>
                    <p><strong>Event Date:</strong> {order.eventDate} at {order.eventTime}</p>
                    <p><strong>Venue:</strong> {order.venue}</p>
                    <p><strong>Guests:</strong> {order.guestCount}</p>
                    {order.budget && <p><strong>Budget:</strong> {order.budget}</p>}
                    <p><strong>Submitted:</strong> {order.createdAt?.toLocaleString()}</p>
                  </div>

                  <div className="order-description">
                    <strong>Event Description:</strong>
                    <p>{order.eventDescription}</p>
                  </div>

                  {order.specialRequirements && (
                    <div className="order-description">
                      <strong>Special Requirements:</strong>
                      <p>{order.specialRequirements}</p>
                    </div>
                  )}

                  <div className="order-actions">
                    <select
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value, 'custom')}
                      className="status-select"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AllOrdersManagement;
